import { SkeletonBlock, SkeletonStack } from '../../../../widgets/skeleton';
import { ZTooltip } from '../../../../widgets/ztooltip';
import React, { FC, Suspense, useState } from 'react';
import { ChevronIcon } from '../../../../widgets/icons/scene';
import { thinScrollbar } from '../../../../widgets/scrollbar';
import styled, { css } from 'styled-components';
import { T } from '../../../../../shared/helpers';

const Container = styled.div<{ $composer?: boolean }>`
    display: flex;
    flex-direction: row;
    height: 100%;
    max-height: 100%;
    overflow: hidden;

    ${(props) =>
        props.$composer &&
        css`
            background: var(--composer-panel, #fff);
            border-radius: 12px;
            box-shadow: var(--composer-shadow, rgba(30, 42, 56, 0.05) 0 1px 2px, rgba(30, 42, 56, 0.06) 0 0 0 1px);
        `}
`;

const MenuContainer = styled.div<{ $expanded?: boolean; $composer?: boolean }>`
    width: ${(props) => (props.$expanded ? '70px' : '43px')};
    flex: 0 0 ${(props) => (props.$expanded ? '70px' : '43px')};
    height: 100%;
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;

    ${(props) =>
        props.$composer &&
        css`
            width: 88px;
            flex: 0 0 88px;
            background: var(--composer-sunken, #f4f7fa);
            border-right: 1px solid var(--composer-line, #dce3eb);
            border-radius: 12px 0 0 12px;
            padding: 6px 5px;
            gap: 2px;
        `}
`;

const LeftVerticalToolbarContainer = styled.div<{ $hiddenSidebar?: boolean; $collapsed?: boolean; $composer?: boolean }>`
    /* NASCOSTO VUOL DIRE NASCOSTO, e oggi lo vuol dire per caso: questo riquadro non dichiara un
       display, quindi vale quello del browser: [hidden] vuol dire display none. Il giorno che
       qualcuno gli desse un display — a un contenitore capita — l'attributo smetterebbe di
       valere IN SILENZIO, perché il foglio d'autore pesa quanto quello del browser e a parità
       vince chi arriva dopo. Questa riga non cambia niente adesso: serve a quel giorno lì. */
    &[hidden] {
        display: none;
    }

    flex: 1;
    min-width: 0;
    height: 100%;
    padding: 24px 16px;
    background: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.backgroundColor};
    max-height: 100%;
    ${(props) =>
        (props.$hiddenSidebar || props.$collapsed) &&
        css`
            background: none;
        `}
    overflow-y: auto;
    ${thinScrollbar}

    ${(props) =>
        props.$composer &&
        css`
            padding: 0;
            background: var(--composer-panel, #fff);

            > section {
                border-radius: 0;
                box-shadow: none;
            }
        `}
`;

// I glifi del prototipo sono a CONTORNO: non passano dal wrapper Icon condiviso,
// che impone `fill: currentColor` e `stroke: inherit` — riempirebbe le figure e
// cancellerebbe il tratto. Qui si dà solo la misura, il resto lo porta il glifo.
const RailGlyph = styled.span`
    display: inline-flex;

    svg {
        width: 20px;
        height: 20px;
        display: block;
    }
`;

const MenuItemContainer = styled.button<{ $active?: boolean; $composer?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    margin: 0;
    border: 0;
    background: transparent;
    color: ${(props) => props.theme.app.textColor};
    padding: 12px 4px;

    &:hover {
        color: ${(props) => props.theme.app.secondary};
    }

    ${(props) =>
        props.$active &&
        css`
            color: ${props.theme.app.secondary};
        `}

    ${(props) =>
        props.$composer &&
        css`
            justify-content: center;
            gap: 3px;
            width: 78px;
            height: 78px;
            flex: 0 0 78px;
            padding: 4px;
            border-radius: 8px;
            color: var(--composer-muted, #5c6b7a);

            &:hover {
                color: var(--composer-fg, #1e2a38);
                background: var(--composer-hover, #e9eff5);
            }

            ${props.$active &&
            css`
                color: var(--composer-accent-text, #a8480b);
                background: rgb(0 0 0 / 6%);
            `}
        `}
`;

const ToolLabel = styled.span<{ $composer?: boolean }>`
    margin-top: 4px;
    font-size: 10px;
    line-height: 1.1;
    text-align: center;

    ${(props) =>
        props.$composer &&
        css`
            display: block;
            width: 100%;
            overflow: hidden;
            margin-top: 0;
            font-size: 11px;
            font-weight: 600;
            line-height: 1.25;
            white-space: normal;
        `}
`;

// Il chevron punta verso il bordo che il pannello raggiungerebbe chiudendosi, e
// si gira quando è già chiuso: il comando per riaprire resta dove stava quello
// che ha chiuso, ed è l'unica chiusura che si sa annullare da sola.
const ClosePanelButton = styled(MenuItemContainer)<{ $collapsed?: boolean }>`
    margin-top: auto;
    margin-bottom: 8px;

    svg {
        transform: rotate(${(props) => (props.$collapsed ? '270deg' : '90deg')});
    }
`;

// I due separatori del rail dividono tre generi — le liste della scena, i
// contenuti della libreria, le proprietà dell'ambiente — e senza di loro nove
// icone in colonna sono un elenco senza capitoli.
const RailSeparator = styled.span`
    width: 22px;
    height: 1px;
    margin: 6px 0;
    background: ${(props) => props.theme.app.borderColor};
`;

const LoaderContainer = styled.div`
    width: 100%;
`;

const ToolbarPanelSkeleton = () => (
    <SkeletonStack aria-hidden='true' $gap='16px'>
        <SkeletonBlock $width='45%' $height='18px' />
        <SkeletonBlock $height='38px' $radius='8px' />
        <SkeletonBlock $height='38px' $radius='8px' />
        <SkeletonBlock $width='70%' $height='14px' $margin='8px 0 0' />
        <SkeletonBlock $height='96px' $radius='8px' />
    </SkeletonStack>
);

const TooltipV2 = styled(ZTooltip)`
    height: 20px;
    margin-left: 3px;
    svg {
        cursor: pointer;
        font-size: 20px;
        color: ${(props) => props.theme.app.secondary};
    }
`;

export interface StepsTools {
    tool: string;
    icon: React.ReactNode;
    tooltip: string;
    onClick: () => void;
    component: React.FC<any>;
    /** Apre un genere nuovo nel rail: sopra la voce compare un separatore. */
    separatorBefore?: boolean;
}

export type LeftVerticalToolbarProps = {
    tools: StepsTools[];
    hiddenSidebar?: boolean;
    selectedTool?: string | null;
    onSelectTool?: (tool: string) => void;
    onClosePanel?: () => void;
    collapsed?: boolean;
    sectionRails?: 'compact' | 'expanded';
    onInspectorChange?: (component: React.ReactNode | null) => void;
    variant?: 'default' | 'composer';
    /**
     * Le sezioni che **non** monta il rail: il loro nodo vive altrove e viene traslocato qui
     * dentro quando tocca a questo attracco. Il rail offre il posto — `contentRef` — e non
     * le costruisce, perché ricostruirle a ogni cambio di attracco vorrebbe dire perderne lo
     * stato (docs/scene-editor-step6-second-layout.md § 1).
     */
    hostedTools?: string[];
    /** Il contenitore in cui il rail mostra la sezione attiva. */
    contentRef?: React.Ref<HTMLDivElement>;
};
const LeftVerticalToolbar: FC<LeftVerticalToolbarProps> = ({
    tools,
    hiddenSidebar,
    selectedTool,
    onSelectTool,
    onClosePanel,
    collapsed,
    sectionRails = 'compact',
    onInspectorChange,
    hostedTools,
    contentRef,
    variant = 'default'
}) => {
    const [internalView, setInternalView] = useState<string | undefined>(tools[0]?.tool);
    const view = selectedTool ?? internalView;
    const isComposer = variant === 'composer';

    return (
        <Container $composer={isComposer}>
            <MenuContainer $expanded={sectionRails === 'expanded'} $composer={isComposer}>
                {tools.map((stepTool, index) => (
                    <React.Fragment key={stepTool.tool}>
                        {stepTool.separatorBefore && index > 0 && <RailSeparator role='presentation' />}
                        <TooltipV2 title={stepTool.tooltip} position='right' addContainer>
                            <MenuItemContainer
                                $active={view === stepTool.tool}
                                $composer={isComposer}
                                onClick={() => {
                                    setInternalView(stepTool.tool);
                                    onSelectTool?.(stepTool.tool);
                                    stepTool.onClick();
                                }}
                                aria-label={stepTool.tool}
                                aria-current={view === stepTool.tool ? 'true' : undefined}
                            >
                                <RailGlyph>{stepTool.icon}</RailGlyph>
                                {(isComposer || sectionRails === 'expanded') && <ToolLabel $composer={isComposer}>{stepTool.tool}</ToolLabel>}
                            </MenuItemContainer>
                        </TooltipV2>
                    </React.Fragment>
                ))}
                {onClosePanel && (
                    <TooltipV2 title={collapsed ? T._('Open panel', 'Admin') : T._('Collapse panel', 'Admin')} position='right' addContainer>
                        <ClosePanelButton
                            type='button'
                            $collapsed={collapsed}
                            aria-expanded={!collapsed}
                            aria-label={collapsed ? T._('Open panel', 'Admin') : T._('Collapse panel', 'Admin')}
                            onClick={onClosePanel}
                        >
                            <RailGlyph>
                                <ChevronIcon />
                            </RailGlyph>
                        </ClosePanelButton>
                    </TooltipV2>
                )}
            </MenuContainer>
            {/* Chiuso, del pannello resta la sola barra: il contenuto non si ritaglia, esce. */}
            <LeftVerticalToolbarContainer
                ref={contentRef}
                $hiddenSidebar={hiddenSidebar}
                $collapsed={collapsed}
                $composer={isComposer}
                hidden={collapsed}
            >
                <Suspense
                    fallback={
                        <LoaderContainer>
                            <ToolbarPanelSkeleton />
                        </LoaderContainer>
                    }
                >
                    {tools.map((stepTool) => {
                        if (view !== stepTool.tool) return null;
                        if (hostedTools?.includes(stepTool.tool)) return null;
                        const ToolComponent = stepTool.component;
                        return <ToolComponent key={stepTool.tool} onInspectorChange={onInspectorChange} />;
                    })}
                </Suspense>
            </LeftVerticalToolbarContainer>
        </Container>
    );
};

export default LeftVerticalToolbar;
